import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { TrainingCourse } from './Training';

interface Page {
  id: number;
  order: number;
  title: string;
  content: string;
  fileUrl?: string | null;
  type_marker: 'page';
}

interface AnswerOption {
  text: string;
  isCorrect?: boolean;
}

interface Question {
  id: number;
  order: number;
  text: string;
  type: 'single' | 'multiple' | 'true_false';
  answers: AnswerOption[];
  type_marker: 'question';
}

type Item = Page | Question;

function isQuestion(item: Item): item is Question {
  return item.type_marker === 'question';
}

interface Props {
  course: TrainingCourse;
  onClose: () => void;
  onComplete: () => void;
}

const COURSE_COLORS: Record<string, { top: string; icon: string }> = {
  security:   { top: 'linear-gradient(90deg,#7c3aed,#a78bfa)', icon: 'rgba(124,58,237,.12)' },
  gdpr:       { top: 'linear-gradient(90deg,#7c3aed,#a78bfa)', icon: 'rgba(124,58,237,.12)' },
  fire:       { top: 'linear-gradient(90deg,#dc2626,#f87171)', icon: 'rgba(220,38,38,.1)' },
  health:     { top: 'linear-gradient(90deg,#059669,#34d399)', icon: 'rgba(5,150,105,.12)' },
  safety:     { top: 'linear-gradient(90deg,#059669,#34d399)', icon: 'rgba(5,150,105,.12)' },
  it:         { top: 'linear-gradient(90deg,#0891b2,#06b6d4)', icon: 'rgba(8,145,178,.12)' },
  equal:      { top: 'linear-gradient(90deg,#db2777,#f472b6)', icon: 'rgba(219,39,119,.1)' },
  diversity:  { top: 'linear-gradient(90deg,#db2777,#f472b6)', icon: 'rgba(219,39,119,.1)' },
  hr:         { top: 'linear-gradient(90deg,#db2777,#f472b6)', icon: 'rgba(219,39,119,.1)' },
  lead:       { top: 'linear-gradient(90deg,#2563eb,#60a5fa)', icon: 'rgba(37,99,235,.12)' },
  mental:     { top: 'linear-gradient(90deg,#d97706,#fbbf24)', icon: 'rgba(217,119,6,.12)' },
  finance:    { top: 'linear-gradient(90deg,#059669,#34d399)', icon: 'rgba(5,150,105,.12)' },
};

function getCourseStyle(title: string) {
  const t = title.toLowerCase();
  for (const [key, val] of Object.entries(COURSE_COLORS)) {
    if (t.includes(key)) return val;
  }
  return { top: 'linear-gradient(90deg,#059669,#0d9488)', icon: 'rgba(5,150,105,.12)' };
}

function getCourseEmoji(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('gdpr') || t.includes('data') || t.includes('security') || t.includes('password')) return '🔒';
  if (t.includes('fire')) return '🧯';
  if (t.includes('health') || t.includes('first aid')) return '🏥';
  if (t.includes('it') || t.includes('cyber') || t.includes('phish')) return '💻';
  if (t.includes('equal') || t.includes('diversity')) return '🤝';
  if (t.includes('mental')) return '🧠';
  if (t.includes('lead')) return '🚀';
  if (t.includes('finance')) return '💰';
  return '📚';
}

const TRAINING_READ_KEY = 'training_read';

function loadRead(courseId: number): Set<number> {
  try {
    const s = localStorage.getItem(TRAINING_READ_KEY);
    if (s) { const obj = JSON.parse(s); return new Set<number>(obj[courseId] ?? []); }
  } catch {}
  return new Set();
}

function saveRead(courseId: number, read: Set<number>) {
  try {
    const s = localStorage.getItem(TRAINING_READ_KEY);
    const obj = s ? JSON.parse(s) : {};
    obj[courseId] = Array.from(read);
    localStorage.setItem(TRAINING_READ_KEY, JSON.stringify(obj));
  } catch {}
}

const CourseViewer: React.FC<Props> = ({ course, onClose, onComplete }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [items, setItems]           = useState<Item[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [readPages, setReadPages]   = useState<Set<number>>(new Set());
  const [selectedAnswers, setSelected] = useState<Record<number, number[]>>({});
  const [submittedQ, setSubmittedQ] = useState<Set<number>>(new Set());
  const [loading, setLoading]       = useState(true);
  const [finished, setFinished]     = useState(false);
  const [finalScore, setFinalScore] = useState<number | null>(null);
  const [open, setOpen]             = useState(false);
  const [isWide, setIsWide]         = useState(() => localStorage.getItem('tr_viewer_wide') === '1');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('tr_sidebar_open') !== '0');
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('tr_sidebar_width') ?? '', 10);
    return isNaN(saved) ? 240 : Math.min(400, Math.max(160, saved));
  });

  const toggleWide = useCallback(() => {
    setIsWide(v => {
      localStorage.setItem('tr_viewer_wide', v ? '0' : '1');
      return !v;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(v => {
      localStorage.setItem('tr_sidebar_open', v ? '0' : '1');
      return !v;
    });
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    const onMove = (ev: MouseEvent) => {
      const newW = Math.min(400, Math.max(160, startW + ev.clientX - startX));
      setSidebarWidth(newW);
    };
    const onUp = (ev: MouseEvent) => {
      const newW = Math.min(400, Math.max(160, startW + ev.clientX - startX));
      localStorage.setItem('tr_sidebar_width', String(newW));
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [sidebarWidth]);

  const color = useMemo(() => getCourseStyle(course.title), [course.title]);
  const emoji = useMemo(() => getCourseEmoji(course.title), [course.title]);

  useEffect(() => {
    setLoading(true);
    axios.get(`training/course/${course.id}`)
      .then(({ data }) => {
        // Content pages first (in their saved order), then quiz questions.
        // Questions have no `order`, so they must not be merged into the same
        // numeric sort as pages — otherwise they all collapse to 0 and jump
        // ahead of every page, pushing the content to the end.
        const pages: Item[] = (data.pages ?? [])
          .map((p: any) => ({ ...p, type_marker: 'page' as const }))
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        const qs: Item[] = (data.questions ?? [])
          .map((q: any) => ({ ...q, type_marker: 'question' as const }))
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        // Pages always first, questions always after — never interleaved
        setItems([...pages, ...qs]);
        setReadPages(loadRead(course.id));
        setLoading(false);
        requestAnimationFrame(() => setOpen(true));
      })
      .catch(() => setLoading(false));
  }, [course.id]);

  // Scroll content to top on page change
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [currentIdx]);

  // Mark current page as read
  useEffect(() => {
    if (!items.length) return;
    const item = items[currentIdx];
    if (!isQuestion(item)) {
      setReadPages(prev => {
        const next = new Set(prev).add(item.id);
        saveRead(course.id, next);
        return next;
      });
    }
  }, [currentIdx, items, course.id]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  const doneCount = useMemo(() =>
    items.filter(item => isQuestion(item) ? submittedQ.has(item.id) : readPages.has(item.id)).length,
    [items, readPages, submittedQ]);

  const progress = items.length > 0 ? Math.round((doneCount / items.length) * 100) : 0;

  // Questions unlock only after ALL content pages are read
  const contentPages = useMemo(() => items.filter(item => !isQuestion(item)), [items]);
  const allPagesRead = useMemo(() =>
    contentPages.length > 0 && contentPages.every(p => readPages.has((p as Page).id)),
    [contentPages, readPages]);

  // Index of first quiz question in the flat items array
  const firstQuizIdx = useMemo(() => items.findIndex(isQuestion), [items]);

  const handleSelectAnswer = (questionId: number, answerIdx: number, isMultiple: boolean) => {
    if (submittedQ.has(questionId)) return;
    setSelected(prev => {
      if (isMultiple) {
        const cur = prev[questionId] ?? [];
        return { ...prev, [questionId]: cur.includes(answerIdx) ? cur.filter(i => i !== answerIdx) : [...cur, answerIdx] };
      }
      return { ...prev, [questionId]: [answerIdx] };
    });
  };

  const handleNext = async () => {
    if (currentIdx < items.length - 1) {
      setCurrentIdx(i => i + 1);
    } else {
      try {
        const answers = items.filter(isQuestion).map(q => ({
          questionId: q.id,
          answer: (selectedAnswers[q.id] ?? []),
        }));
        const { data } = await axios.post('training/result', { courseId: course.id, answers });
        setFinalScore(data.score ?? null);
        onComplete(); // refresh course list in background; viewer stays open so user sees the result
      } catch {}
      setFinished(true);
    }
  };

  const currentItem = items[currentIdx];
  const isNextEnabled = currentItem
    ? isQuestion(currentItem) ? submittedQ.has(currentItem.id) : true
    : false;

  const renderItem = () => {
    if (!currentItem) return null;

    if (isQuestion(currentItem)) {
      const isMultiple = currentItem.type === 'multiple';
      const selected = selectedAnswers[currentItem.id] ?? [];
      const submitted = submittedQ.has(currentItem.id);
      const allCorrect = submitted && currentItem.answers.every((a, i) =>
        (a.isCorrect === true) === selected.includes(i));

      return (
        <div>
          <div className="tr-page-title">
            {currentItem.type === 'multiple' ? 'Multiple Choice' : currentItem.type === 'true_false' ? 'True / False' : 'Knowledge Check'}
          </div>
          <div className="tr-page-sub">
            <span style={{ background: 'rgba(5,150,105,.1)', color: '#059669', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
              Page {currentIdx + 1} of {items.length}
            </span>
            {isMultiple && <span>Select all that apply</span>}
          </div>
          <div className="tr-quiz-wrap">
            <div className="tr-quiz-label">
              <span style={{ background: 'rgba(5,150,105,.15)', padding: '2px 8px', borderRadius: 6 }}>Quiz question</span>
            </div>
            <div className="tr-quiz-q">{currentItem.text}</div>
            <div className="tr-quiz-options">
              {currentItem.answers.map((ans, idx) => {
                const isSel = selected.includes(idx);
                const isCorrectOpt = ans.isCorrect === true;
                let cls = 'tr-quiz-option';
                if (submitted) {
                  if (isCorrectOpt) cls += ' correct';
                  else if (isSel) cls += ' wrong';
                  else cls += ' disabled';
                } else if (isSel) cls += ' selected';
                return (
                  <div key={idx} className={cls}
                    onClick={() => handleSelectAnswer(currentItem.id, idx, isMultiple)}>
                    <div className="tr-quiz-radio">
                      {submitted && isCorrectOpt && <span style={{ fontSize: 10, color: 'white', fontWeight: 900 }}>✓</span>}
                      {submitted && isSel && !isCorrectOpt && <span style={{ fontSize: 10, color: 'white', fontWeight: 900 }}>✗</span>}
                      {!submitted && isSel && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', display: 'block' }} />}
                    </div>
                    {ans.text}
                  </div>
                );
              })}
            </div>
            {submitted && (
              <div className={`tr-quiz-result ${allCorrect ? 'pass' : 'fail'}`}>
                {allCorrect ? '✓ Correct! Well done.' : '✗ Incorrect. The correct answer is highlighted above.'}
              </div>
            )}
            {!submitted && selected.length > 0 && (
              <button type="button" className="tr-viewer-btn-primary" style={{ marginTop: 8 }}
                onClick={() => setSubmittedQ(prev => new Set(prev).add(currentItem.id))}>
                Submit answer
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="tr-page-title">{currentItem.title}</div>
        <div className="tr-page-sub">
          <span style={{ background: 'rgba(5,150,105,.1)', color: '#059669', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800 }}>
            Page {currentIdx + 1} of {items.length}
          </span>
        </div>
        {currentItem.fileUrl && (
          <img src={currentItem.fileUrl} alt={currentItem.title}
            style={{ width: '100%', borderRadius: 12, marginBottom: 20, maxHeight: 300, objectFit: 'cover' }} />
        )}
        <div className="tr-content-body"
          dangerouslySetInnerHTML={{ __html: currentItem.content || '<p>No content yet.</p>' }} />
      </div>
    );
  };

  const panel = (
    <div className={`tr-viewer${open ? ' open' : ''}${isWide ? ' wide' : ''}`}>
      {/* Header */}
      <div className="tr-viewer-header">
        <button type="button" className="tr-viewer-back" onClick={handleClose}>←</button>
        <div className="tr-viewer-course-ic" style={{ background: color.icon }}>{emoji}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tr-viewer-title">{course.title}</div>
          <div className="tr-viewer-sub">
            <span>{course.pageCount} pages · {course.questionCount} quizzes</span>
          </div>
        </div>
        <div className="tr-viewer-prog">
          <div className="tr-viewer-prog-lbl">
            <span>Page {currentIdx + 1} of {items.length}</span>
            <span style={{ color: '#059669', fontWeight: 700 }}>{progress}%</span>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: 'var(--border-input)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 3, width: `${progress}%`, background: color.top, transition: 'width .6s' }} />
          </div>
        </div>
        <button type="button" className="tr-viewer-wide-btn" onClick={toggleWide} title={isWide ? 'Exit fullscreen' : 'Fullscreen'}>
          {isWide ? (
            <svg viewBox="0 0 24 24"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>
          ) : (
            <svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
          )}
        </button>
        <button type="button" className="tr-viewer-close" onClick={handleClose}>✕</button>
      </div>

      {/* Body */}
      <div className="tr-viewer-body">
        {/* Sidebar — hidden when sidebarOpen=false */}
        {sidebarOpen && (
          <div className="tr-viewer-sidebar" style={{ width: sidebarWidth }}>
            <div className="tr-sidebar-head">
              Course Content
              <button type="button" className="tr-sidebar-collapse-btn" onClick={toggleSidebar} title="Hide sidebar">«</button>
            </div>
            <div className="tr-chapters">
              {loading
                ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>Loading…</div>
                : items.map((item, i) => {
                    const isDone = isQuestion(item) ? submittedQ.has(item.id) : readPages.has(item.id);
                    const isActive = i === currentIdx;
                    // Questions locked until all content pages are read; pages locked sequentially
                    const isLocked = isQuestion(item)
                      ? !allPagesRead && !isDone
                      : (!isDone && !isActive && i > currentIdx);
                    return (
                      <React.Fragment key={`${item.type_marker}-${item.id}`}>
                        {/* Quiz section separator before first question */}
                        {i === firstQuizIdx && firstQuizIdx > 0 && (
                          <div className="tr-quiz-sep">
                            <span>{allPagesRead ? '📋 Quiz' : '🔒 Quiz — read all pages first'}</span>
                          </div>
                        )}
                        <div
                          className={`tr-chapter-item${isDone ? ' done' : ''}${isActive ? ' active' : ''}${isLocked ? ' locked' : ''}`}
                          onClick={() => !isLocked && setCurrentIdx(i)}>
                          <div className="tr-chapter-num">{isDone ? '✓' : isQuestion(item) ? '✦' : i + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="tr-chapter-title">
                              {isQuestion(item)
                                ? `${item.text.length > 32 ? item.text.slice(0, 32) + '…' : item.text}`
                                : item.title}
                            </div>
                          </div>
                          <div className="tr-chapter-time">{isQuestion(item) ? 'quiz' : '~3 min'}</div>
                        </div>
                      </React.Fragment>
                    );
                  })}
            </div>
          </div>
        )}

        {/* Drag handle — only in fullscreen when sidebar is open */}
        {isWide && sidebarOpen && (
          <div className="tr-drag-handle" onMouseDown={handleDragStart} />
        )}

        {/* Show sidebar button — only when sidebar is hidden */}
        {!sidebarOpen && (
          <button type="button" className="tr-sidebar-show-btn" onClick={toggleSidebar} title="Show sidebar">»</button>
        )}

        {/* Content column: scrollable area + nav footer */}
        <div className="tr-viewer-content-col">
          <div className="tr-viewer-content" ref={contentRef}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>Loading course…</div>
            ) : finished ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>
                  {finalScore !== null && finalScore >= (course.passThreshold ?? 80) ? '🎉' : '😕'}
                </div>
                <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--text-color)', marginBottom: 8 }}>
                  {finalScore !== null && finalScore >= (course.passThreshold ?? 80) ? 'Course Complete!' : 'Keep practising'}
                </div>
                {finalScore !== null && (
                  <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: '-.03em', marginBottom: 12,
                    color: finalScore >= (course.passThreshold ?? 80) ? '#059669' : '#dc2626' }}>
                    {Math.round(finalScore)}%
                  </div>
                )}
                <div style={{ fontSize: 14, color: 'var(--text-color-calendar)', marginBottom: 28 }}>
                  {finalScore !== null && finalScore >= (course.passThreshold ?? 80)
                    ? 'Congratulations! You have passed this course.'
                    : `Pass mark is ${course.passThreshold ?? 80}%. Try again when ready.`}
                </div>
                <button type="button" className="tr-viewer-btn-primary" onClick={handleClose}>
                  ← Back to Training
                </button>
              </div>
            ) : (
              renderItem()
            )}
          </div>

          {/* Nav — under content only, left button aligned to left edge of content, right to right */}
          {!loading && !finished && (
            <div className="tr-viewer-nav">
              <button type="button" className="tr-viewer-btn-ghost"
                onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                disabled={currentIdx === 0}
                style={{ opacity: currentIdx === 0 ? 0.4 : 1, cursor: currentIdx === 0 ? 'not-allowed' : 'pointer' }}>
                ← Previous
              </button>
              <button type="button" className="tr-viewer-btn-primary"
                onClick={handleNext}
                disabled={!isNextEnabled}
                style={{ opacity: isNextEnabled ? 1 : 0.4, cursor: isNextEnabled ? 'pointer' : 'not-allowed' }}>
                {currentIdx === items.length - 1 ? 'Finish course ✓' : 'Next →'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default CourseViewer;
